import { legAirExcise } from "./calculator";
import {
  effectiveFlightHours,
  effectiveTakeoffsCount,
  effectiveTexasLandings,
  isTexasDepartureLeg,
  type TexasLandingOptions,
  personalFuelOwed2x,
} from "./fuelDerived";
import type { FlightLeg, Rates } from "./types";

/** Program variable charges (editable on the Maintenance tab). */
export const DEFAULT_HONDA_USD_PER_FLIGHT_HOUR = 305;
export const DEFAULT_HONDA_USD_PER_LANDING = 260;

/** Reference monthly program charge (display / docs only; not part of Form 720 totals). */
export const DEFAULT_HONDA_USD_MONTHLY_PROGRAM = 1600;

export type CompanyHondaSummary = {
  totalTakeoffs: number;
  /** Takeoffs where departure is TX (<code>departure_state</code> and/or origin ICAO infer). */
  takeoffsTexas: number;
  totalLandings: number;
  totalFlightHours: number;
  hoursBusiness: number;
  hoursPersonal: number;
  tripsTotal: number;
  tripsBusiness: number;
  tripsPersonal: number;
  pctTripsBusiness: number | null;
  pctHoursBusiness: number | null;
  hondaHourlyCharge: number;
  hondaLandingCharge: number;
  hondaVariableSubtotal: number;
  /** Sum of IRS 26+27 on personal legs (payment to Utah Gas Corp for air excise). */
  personalAirExciseReimburseTotal: number;
  totalTexasLandings: number;
  /** Personal legs: 2× fuel cost to Utah Gas Corp (when price/gal present). */
  personalFuelOwed2xTotal: number;
  /** Sum of <code>passengerCount</code> on personal-classified legs (≥ 0 per leg). */
  personalPassengerCountSum: number;
};

function effectiveLandings(leg: FlightLeg): number {
  const n = leg.landings;
  if (n != null && Number.isFinite(n) && n >= 0) {
    return Math.max(0, Math.round(n));
  }
  return 1;
}

/**
 * Operations + Honda variable charges + personal air-excise reimbursement total.
 * Business vs personal: by trip = Personal checkbox; by hours = same split on legs that have flight hours filled.
 */
export type CompanyHondaOptions = {
  isPersonalLeg?: (leg: FlightLeg) => boolean;
  texasLanding?: TexasLandingOptions;
};

export function summarizeCompanyHonda(
  legs: FlightLeg[],
  rates: Rates,
  hondaHourlyUsd: number,
  hondaLandingUsd: number,
  opts?: CompanyHondaOptions
): CompanyHondaSummary {
  const isPersonal = opts?.isPersonalLeg ?? ((leg: FlightLeg) => leg.personalUse);
  const txOpts = opts?.texasLanding;
  let totalTakeoffs = 0;
  let takeoffsTexas = 0;
  let totalLandings = 0;
  let totalFlightHours = 0;
  let hoursBusiness = 0;
  let hoursPersonal = 0;
  let personalAirExciseReimburseTotal = 0;
  let totalTexasLandings = 0;
  let personalFuelOwed2xTotal = 0;
  let personalPassengerCountSum = 0;

  const tripsTotal = legs.length;
  let tripsBusiness = 0;
  let tripsPersonal = 0;

  for (const leg of legs) {
    const to = effectiveTakeoffsCount(leg);
    const ld = effectiveLandings(leg);
    totalTakeoffs += to;
    totalLandings += ld;
    if (isTexasDepartureLeg(leg, txOpts)) takeoffsTexas += to;

    const h = effectiveFlightHours(leg);
    if (h !== null) {
      totalFlightHours += h;
      if (isPersonal(leg)) hoursPersonal += h;
      else hoursBusiness += h;
    }

    totalTexasLandings += effectiveTexasLandings(leg, txOpts);
    personalFuelOwed2xTotal += personalFuelOwed2x(leg, rates, isPersonal(leg));

    if (isPersonal(leg)) {
      tripsPersonal += 1;
      personalPassengerCountSum += Math.max(0, Math.trunc(leg.passengerCount));
      personalAirExciseReimburseTotal += legAirExcise(leg, rates);
    } else {
      tripsBusiness += 1;
    }
  }

  const pctTripsBusiness =
    tripsTotal > 0 ? (100 * tripsBusiness) / tripsTotal : null;
  const hoursRecorded = hoursBusiness + hoursPersonal;
  const pctHoursBusiness =
    hoursRecorded > 0 ? (100 * hoursBusiness) / hoursRecorded : null;

  const hondaHourlyCharge = hondaHourlyUsd * totalFlightHours;
  const hondaLandingCharge = hondaLandingUsd * totalLandings;
  const hondaVariableSubtotal = hondaHourlyCharge + hondaLandingCharge;

  return {
    totalTakeoffs,
    takeoffsTexas,
    totalLandings,
    totalFlightHours,
    hoursBusiness,
    hoursPersonal,
    tripsTotal,
    tripsBusiness,
    tripsPersonal,
    pctTripsBusiness,
    pctHoursBusiness,
    hondaHourlyCharge,
    hondaLandingCharge,
    hondaVariableSubtotal,
    personalAirExciseReimburseTotal,
    totalTexasLandings,
    personalFuelOwed2xTotal,
    personalPassengerCountSum,
  };
}
